import React, { useEffect, useState } from 'react';
import cn from '@/utils/cn';
import SPADropdown from './shared/SPADropdown';

const THEME_KEY = 'spa-theme';
type Theme = 'dark' | 'light' | 'system';

interface ThemeOption {
  icon: React.ReactNode;
  label: string;
  value: Theme;
}

const iconClass = 'tw-w-4 tw-h-4';

const themeOptions: ThemeOption[] = [
  {
    icon: (
      <svg className={iconClass} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <rect height="13" rx="2" width="20" x="2" y="3" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    label: 'System',
    value: 'system',
  },
  {
    icon: (
      <svg className={iconClass} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
      </svg>
    ),
    label: 'Light',
    value: 'light',
  },
  {
    icon: (
      <svg className={iconClass} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
      </svg>
    ),
    label: 'Dark',
    value: 'dark',
  },
];

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';

  return (localStorage.getItem(THEME_KEY) as Theme) || 'system';
};

const applyTheme = (theme: Theme) => {
  const html = document.documentElement;

  if (theme === 'system') {
    html.removeAttribute('data-theme');
  } else {
    html.setAttribute('data-theme', theme);
  }
};

const ThemeSelector = () => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    applyTheme(theme);
    if (theme === 'system') {
      localStorage.removeItem(THEME_KEY);
    } else {
      localStorage.setItem(THEME_KEY, theme);
    }
  }, [theme]);

  const activeOption = themeOptions.find(o => o.value === theme)!;

  return (
    <SPADropdown open={open} onOpenChange={setOpen}>
      <SPADropdown.Trigger asChild>
        <button
          aria-expanded={open}
          aria-label="Theme"
          className={cn(
            'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-text-sm',
            'tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded',
            'tw-text-spa-secondary hover:tw-text-spa-high-contrast',
          )}
          type="button"
        >
          {activeOption.icon}
          {/* AIDEV-NOTE: Icon only until reflow-narrow. The nav bar is one row, so every label on the
              right edge is width the tab strip does not get — and at 320px the strip is the thing
              starving. The icon already carries the state (monitor/sun/moon), and aria-label="Theme"
              above is what screen readers announce either way, so nothing is lost by dropping it. */}
          <span className="tw-hidden reflow-narrow:tw-inline">{activeOption.label}</span>
          <svg
            className={cn(
              'tw-w-3.5 tw-h-3.5 tw-fill-current tw-opacity-75 tw-transition-transform',
              'tw-hidden reflow-narrow:tw-block',
              open && 'tw-rotate-180',
            )}
            viewBox="0 0 12 12"
          >
            <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
          </svg>
        </button>
      </SPADropdown.Trigger>
      {/* Radio semantics, not styling: these are mutually exclusive, so each row needs
          role="menuitemradio" + aria-checked rather than just a brighter label. */}
      <SPADropdown.Content className="tw-w-36">
        <SPADropdown.RadioGroup value={theme} onValueChange={value => setTheme(value as Theme)}>
          {themeOptions.map(option => (
            <SPADropdown.RadioItem key={option.value} value={option.value}>
              {option.icon}
              {option.label}
            </SPADropdown.RadioItem>
          ))}
        </SPADropdown.RadioGroup>
      </SPADropdown.Content>
    </SPADropdown>
  );
};

export default ThemeSelector;
